import type { LocalDate } from '../../platform/localDate'

export type { LocalDate }
export type Granularity = 'month' | 'quarter' | 'year'

export const GRANULARITIES: readonly { value: Granularity; label: string }[] = [
  { value: 'month', label: 'Monthly' },
  { value: 'quarter', label: 'Quarterly' },
  { value: 'year', label: 'Yearly' },
]

/** `period` is the unit's first day. */
export type CashflowView = { granularity: Granularity; period: LocalDate }

const MONTHS_IN: Record<Granularity, number> = { month: 1, quarter: 3, year: 12 }
/** Ten years of units, the current one included. */
const HISTORY_UNITS: Record<Granularity, number> = { month: 120, quarter: 40, year: 10 }

const pad = (n: number): string => String(n).padStart(2, '0')

function parts(d: LocalDate): { year: number; month: number; day: number } {
  const [year = 0, month = 1, day = 1] = d.split('-').map(Number)
  return { year, month, day }
}

const fromIndex = (index: number, day = 1): LocalDate =>
  `${String(Math.floor(index / 12)).padStart(4, '0')}-${pad((index % 12) + 1)}-${pad(day)}`

const monthIndex = (d: LocalDate): number => {
  const { year, month } = parts(d)
  return year * 12 + (month - 1)
}

export function unitStart(d: LocalDate, g: Granularity): LocalDate {
  const size = MONTHS_IN[g]
  return fromIndex(Math.floor(monthIndex(d) / size) * size)
}

export function unitEnd(start: LocalDate, g: Granularity): LocalDate {
  const nextStart = fromIndex(monthIndex(start) + MONTHS_IN[g])
  const { year, month } = parts(nextStart)
  // Day 0 of the next unit is the natural last day of this one.
  const last = new Date(Date.UTC(year, month - 1, 0)).getUTCDate()
  return fromIndex(monthIndex(nextStart) - 1, last)
}

export function step(start: LocalDate, g: Granularity, n: number): LocalDate {
  return fromIndex(monthIndex(start) + n * MONTHS_IN[g])
}

export function clampPeriod(
  start: LocalDate,
  g: Granularity,
  earliest: LocalDate | null,
  today: LocalDate,
): LocalDate {
  const snapped = unitStart(start, g)
  const upper = unitStart(today, g)
  if (snapped > upper) return upper
  if (earliest !== null) {
    const lower = unitStart(earliest, g)
    if (snapped < lower) return lower
  }
  return snapped
}

export function convertPeriod(
  start: LocalDate,
  from: Granularity,
  to: Granularity,
  today: LocalDate,
): LocalDate {
  if (MONTHS_IN[to] < MONTHS_IN[from]) {
    // Going finer: stay on today's unit when today is inside the old period.
    const containsToday = today >= start && today <= unitEnd(start, from)
    return containsToday ? unitStart(today, to) : unitStart(start, to)
  }
  return unitStart(start, to)
}

export function periodWindow(
  start: LocalDate,
  g: Granularity,
  today: LocalDate,
): { start: LocalDate; end: LocalDate; partial: boolean } {
  const end = unitEnd(start, g)
  if (today >= start && today <= end) return { start, end: today, partial: true }
  return { start, end, partial: false }
}

export function historyWindow(g: Granularity, today: LocalDate): { start: LocalDate; end: LocalDate } {
  return { start: step(unitStart(today, g), g, -(HISTORY_UNITS[g] - 1)), end: today }
}

/**
 * The oldest period the page may show, and where the history call starts: the
 * later of the earliest transaction's unit and the start of the ten-year
 * history (R9). Null with no earliest transaction to bound by.
 */
export function lowerBound(earliest: LocalDate | null, g: Granularity, today: LocalDate): LocalDate | null {
  if (earliest === null) return null
  const own = unitStart(earliest, g)
  const history = historyWindow(g, today).start
  return own > history ? own : history
}

/** The one month-name table for the module (`cashflowRules` reads it too). */
export const MONTH_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
export const MONTH_LONG = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
]

const monthLong = (d: LocalDate): string => MONTH_LONG[parts(d).month - 1] ?? ''
const monthShort = (d: LocalDate): string => MONTH_SHORT[parts(d).month - 1] ?? ''
const dayNum = (d: LocalDate): string => String(parts(d).day)
const dayMonth = (d: LocalDate): string => `${dayNum(d)} ${monthShort(d)}`

/** `'1 Nov 2025'`: a calendar date in words, from its parts. */
export function dayMonthYear(d: LocalDate): string {
  return `${dayMonth(d)} ${parts(d).year}`
}

export function periodTitle(start: LocalDate, g: Granularity): string {
  const { year, month } = parts(start)
  if (g === 'year') return String(year)
  if (g === 'quarter') return `Q${Math.floor((month - 1) / 3) + 1} ${year}`
  return `${monthLong(start)} ${year}`
}

/**
 * The comparison caption, decided from the prior's own dates: a whole unit is
 * named, anything shorter gives its days. A year is appended only when the prior
 * sits in a different year from the period after it (the selected one).
 */
export function priorPhrase(prior: { start: LocalDate; end: LocalDate }, g: Granularity): string {
  const priorYear = parts(prior.start).year
  const suffix = priorYear === parts(step(prior.start, g, 1)).year ? '' : ` ${priorYear}`
  const whole = prior.start === unitStart(prior.start, g) && prior.end === unitEnd(prior.start, g)
  if (whole) {
    if (g === 'month') return `vs ${monthLong(prior.start)}${suffix}`
    return `vs ${periodTitle(prior.start, g)}`
  }
  if (monthIndex(prior.start) === monthIndex(prior.end)) {
    return `vs ${dayNum(prior.start)}–${dayNum(prior.end)} ${monthShort(prior.end)}${suffix}`
  }
  return `vs ${dayMonth(prior.start)} – ${dayMonth(prior.end)}${suffix}`
}

const isGranularity = (v: string | null): v is Granularity => GRANULARITIES.some((g) => g.value === v)

function validDate(v: string | null): LocalDate | null {
  if (v === null || !/^\d{4}-\d{2}-\d{2}$/.test(v)) return null
  const { year, month, day } = parts(v)
  const t = new Date(Date.UTC(year, month - 1, day))
  const ok = t.getUTCFullYear() === year && t.getUTCMonth() === month - 1 && t.getUTCDate() === day
  return ok ? v : null
}

export function readView(params: URLSearchParams, today: LocalDate): CashflowView {
  const raw = params.get('granularity')
  const granularity: Granularity = isGranularity(raw) ? raw : 'month'
  const given = validDate(params.get('period'))
  return { granularity, period: clampPeriod(given ?? today, granularity, null, today) }
}

export function writeView(view: CashflowView, today: LocalDate): URLSearchParams {
  const out = new URLSearchParams()
  if (view.granularity !== 'month') out.set('granularity', view.granularity)
  if (view.period !== unitStart(today, view.granularity)) out.set('period', view.period)
  return out
}
